import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Keyboard } from "@phosphor-icons/react";
import { armGNav, clearGNav } from "../lib/keyboardState";

/**
 * Mapping for `g <letter>` Gmail/Linear-style nav shortcuts.
 * Hold or quickly tap `g` then a second letter (within 1.2s) to navigate.
 */
const G_NAV = {
  d: { path: "/",                label: "Dashboard" },
  r: { path: "/job-requests",    label: "Requests" },
  j: { path: "/jobs",            label: "Jobs" },
  e: { path: "/emergency-jobs",  label: "Emergency Jobs" },
  p: { path: "/projects",        label: "Projects" },
  q: { path: "/quotes",          label: "Quotes" },
  i: { path: "/invoices",        label: "Invoices" },
  c: { path: "/clients",         label: "Clients" },
  s: { path: "/vendors",         label: "Suppliers" },
  t: { path: "/timeline",        label: "Timeline" },
  f: { path: "/files",           label: "File Hub" },
};

function isTypingTarget(t) {
  if (!t) return false;
  const tag = (t.tagName || "").toLowerCase();
  return tag === "input" || tag === "textarea" || tag === "select" || t.isContentEditable;
}

/**
 * Global keyboard-shortcut cheatsheet. Opens when the user presses `?`
 * (Shift + / on most keyboards) from anywhere that isn't an input field.
 *
 * Also handles `g <letter>` navigation hotkeys (Gmail/Linear style).
 */
export default function ShortcutsCheatsheet() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const gArmedAt = useRef(0);

  useEffect(() => {
    const handler = (e) => {
      if (isTypingTarget(e.target)) return;
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      // ? → cheatsheet
      if (e.key === "?") {
        e.preventDefault();
        setOpen(true);
        return;
      }

      // g <letter> → navigate
      const now = Date.now();
      if (e.key === "g" || e.key === "G") {
        gArmedAt.current = now;
        armGNav();
        return;
      }
      if (gArmedAt.current && now - gArmedAt.current < 1200) {
        const target = G_NAV[(e.key || "").toLowerCase()];
        gArmedAt.current = 0;
        clearGNav();
        if (target) {
          e.preventDefault();
          navigate(target.path);
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [navigate]);

  // Mac vs non-Mac display
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/i.test(navigator.platform || "");
  const mod = isMac ? "⌘" : "Ctrl";

  const sections = [
    {
      title: "Global",
      items: [
        { label: "Open global search", keys: [mod, "K"] },
        { label: "Open Quick Create menu", keys: ["C"] },
        { label: "Show this cheatsheet", keys: ["?"] },
        { label: "Close dialog / cancel", keys: ["Esc"] },
      ],
    },
    {
      title: "Go to",
      items: Object.entries(G_NAV).map(([letter, { label }]) => ({
        label, keys: ["G", letter.toUpperCase()],
      })),
    },
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="shortcuts-cheatsheet">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-[Chivo]">
            <Keyboard size={18} className="text-primary" />
            Keyboard Shortcuts
          </DialogTitle>
          <DialogDescription>Quick keys to fly through the platform.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {sections.map(sec => (
            <div key={sec.title}>
              <h3 className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">{sec.title}</h3>
              <ul className="space-y-1.5">
                {sec.items.map(item => (
                  <li key={item.label} className="flex items-center justify-between text-sm">
                    <span>{item.label}</span>
                    <span className="flex items-center gap-1">
                      {item.keys.map((k, i) => (
                        <kbd
                          key={`${item.label}-${i}`}
                          className="inline-flex h-6 min-w-[24px] items-center justify-center rounded border border-border bg-muted/50 px-1.5 text-xs font-semibold text-foreground"
                        >
                          {k}
                        </kbd>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <p className="text-[11px] text-muted-foreground mt-3 pt-3 border-t border-border">
          Tap <kbd className="rounded border border-border bg-muted/50 px-1 text-[10px]">G</kbd> then a letter (within 1.2s) to navigate. Shortcuts are suppressed while typing in fields or when a dialog is open.
        </p>
      </DialogContent>
    </Dialog>
  );
}
